import { useCallback, useEffect, useState } from "react";
import { toast } from "react-toastify";
import { useTranslate } from "../../../../components/TranslationText";
import { fetchMyEmbryoTransferPermitsApi } from "../Api/embryoTransferPermitsApi";
import { DEFAULT_PAGE_SIZE } from "../../../../constants/serviceListConstants";

/**
 * Custom hook managing Embryo Transfer Permit Requests state, server-side API fetching, filtering, and pagination.
 * Follows the AllServicesRequestsPage pattern with server-side searchTerm and status filtering.
 */
export const useEmbryoTransferPermitRequests = () => {
    const { translate } = useTranslate();
    const currentLanguage = localStorage.getItem("eahsLang") ?? "en";

    const [permits, setPermits] = useState([]);
    const [loading, setLoading] = useState(true);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalCount, setTotalCount] = useState(0);

    // Filters
    const [searchTerm, setSearchTerm] = useState("");
    const [selectedStatus, setSelectedStatus] = useState("all");

    // Server-side fetch data from API
    const fetchPermits = useCallback(async (page = 1, status = selectedStatus, keyword = searchTerm) => {
        setLoading(true);
        try {
            const cleanKeyword = (keyword || "").trim();
            const response = await fetchMyEmbryoTransferPermitsApi({
                pageNumber: page,
                pageSize: DEFAULT_PAGE_SIZE,
                status: status === "all" ? "" : status,
                searchTerm: cleanKeyword,
            });

            const list = response?.result ?? [];

            const total = response?.paginationInfo?.totalCount;

            setPermits(list);
            setTotalCount(total);
        } catch (error) {
            console.error("Failed to load embryo transfer permits:", error);
            toast.error(translate("web.somethingWentWrong", "Something went wrong"));
            setPermits([]);
            setTotalCount(0);
        } finally {
            setLoading(false);
        }
    }, [selectedStatus, searchTerm, translate]);

    // Initial fetch and language change
    useEffect(() => {
        fetchPermits(1, "all", "");
        setCurrentPage(1);
    }, [currentLanguage]); // eslint-disable-line react-hooks/exhaustive-deps

    const handleSearchChange = (e) => {
        const val = e?.target?.value ?? "";
        setSearchTerm(val);
        if (!val.trim()) {
            setCurrentPage(1);
            fetchPermits(1, selectedStatus, "");
        }
    };

    const handleSearchSubmit = () => {
        const keyword = (searchTerm || "").trim();
        setCurrentPage(1);
        fetchPermits(1, selectedStatus, keyword);
    };

    const handleClearSearch = () => {
        setSearchTerm("");
        setCurrentPage(1);
        fetchPermits(1, selectedStatus, "");
    };

    const handleStatusChange = (status) => {
        setSelectedStatus(status);
        setCurrentPage(1);
        fetchPermits(1, status, searchTerm);
    };

    const totalPages = Math.max(1, Math.ceil(totalCount / DEFAULT_PAGE_SIZE));
    const isLastPage = permits.length < DEFAULT_PAGE_SIZE;

    const goToPage = (newPage) => {
        if (newPage < 1 || (newPage > totalPages && isLastPage)) return;
        setCurrentPage(newPage);
        fetchPermits(newPage, selectedStatus, searchTerm);
        window.scrollTo({ top: 0, behavior: "smooth" });
    };

    const refresh = () => {
        fetchPermits(currentPage, selectedStatus, searchTerm);
    };

    return {
        permits, // Direct server data (no client-side filtering)
        loading,
        currentPage,
        totalPages,
        isLastPage,
        searchTerm,
        selectedStatus,
        handleSearchChange,
        handleSearchSubmit,
        handleClearSearch,
        handleStatusChange,
        goToPage,
        refresh,
    };
};
